import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthSevices } from '@fitness-force/api';
import { FeatureFlagService } from '@fitness-force/shared-utils';

// Sidebar Service
@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private featureFlagService = inject(FeatureFlagService);
  private authService = inject(AuthSevices);

  private menusSubject = new BehaviorSubject<Array<any>>([]);

  public getMenus(): any[] {
    return this.menusSubject.value;
  }

  public setMenus(menus: any) {
    this.menusSubject.next(menus);
  }

  public resetMenus(initialMenus: any[]) {
    this.menusSubject.next([...initialMenus]);
  }

  public async updateMenuWithFlag(
    menus: any[],
    mainTitle: string,
    flag: string,
    submenu: any,
  ): Promise<any[]> {
    // Wait for Statsig initialization before checking flag
    const isEnabled = await this.featureFlagService.isEnabledAsync(flag);

    if (isEnabled) {
      menus.forEach((menu) => {
        if (menu.activeUrl === mainTitle) {
          const submenuIndex = menu.submenus.findIndex(
            (sub: { title: any }) => sub.title === submenu.title,
          );
          if (submenuIndex === -1) {
            menu.submenus.unshift({ ...submenu, activeUrl: menu.activeUrl });
          }
        }
      });

      menus.forEach((menu) => {
        if (menu.activeUrl === mainTitle) {
          menu.submenus.sort((a: { title: string; order?: number }, b: { title: string; order?: number }) => {
            const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            return a.title.localeCompare(b.title);
          });
        }
      });
    }
    this.menusSubject.next(menus);
    return menus;
  }

  public async updateMenusBasedOnStatsigState(statsigUser: any): Promise<void> {
    if (statsigUser) {
      // Wait for Statsig initialization
      await this.featureFlagService.waitForInitialization();

      const menus = this.getMenus();

      if (await this.featureFlagService.isEnabledAsync('show_dashboard')) {
        if (!menus.find((value: any) => value.name === 'dashboard')) {
          menus.unshift({
            title: 'DASHBOARD',
            name: 'dashboard',
            url: '',
            activeUrl: 'dashboard',
            icon: 'fa-gauge',
            icon2: '',
            submenus: [
              {
                title: 'SALES',
                name: 'sales',
                url: '/dashboard/sales',
                activeUrl: '/dashboard/sales',
                icon: 'fa-credit-card',
                disabled: false,
              },
              {
                title: 'PT',
                name: 'pt',
                url: '/dashboard/pt',
                activeUrl: '/dashboard/pt',
                icon: 'fa-sync',
                disabled: false,
              },
              {
                title: 'OPERATIONAL',
                name: 'operational',
                url: '/dashboard/operational',
                activeUrl: '/dashboard/operational',
                icon: 'fa-cog',
                disabled: false,
              },
            ],
          });
        }
      }

      if (await this.featureFlagService.isEnabledAsync('show_campaign')) {
        if (!menus.find((value: any) => value.name === 'campaign')) {
          menus.push({
            title: 'CAMPAIGN',
            name: 'campaign',
            url: '/campaign',
            activeUrl: '/campaign',
            icon: 'fa-bullhorn',
          });
        }
      }

      if (
        await this.featureFlagService.isEnabledAsync('show_campaign_automation')
      ) {
        if (!menus.find((value: any) => value.name === 'automation')) {
          menus.push({
            title: 'AUTOMATION',
            name: 'automation',
            url: '/automation',
            activeUrl: '/automation',
            icon: 'fa-users-gear',
          });
        }
      }

      if (await this.featureFlagService.isEnabledAsync('show_payoutengine')) {
        if (!menus.find((value: any) => value.name === 'payout-engine')) {
          menus.push({
            title: 'PAYOUT_ENGINE',
            name: 'payout-engine',
            url: '',
            activeUrl: '/payout-engine',
            icon: 'fa-money-bill-wave',
            icon2: '',
            disabled: false,
            submenus: [
              {
                order: 1,
                title: 'PAYOUT_RULES',
                name: 'payout-rules',
                url: '/payout-engine/rules',
                activeUrl: '/payout-engine',
                icon: 'fa-cog',
                disabled: false,
              },
              {
                order: 2,
                title: 'PAYOUT_CYCLES',
                name: 'payout-cycles',
                url: '/payout-engine/cycles',
                activeUrl: '/payout-engine',
                icon: 'fa-sync',
                disabled: false,
              },
              {
                order: 3,
                title: 'DEBT_OBLIGATIONS',
                name: 'debt-obligations',
                url: '/payout-engine/obligations',
                activeUrl: '/payout-engine',
                icon: 'fa-file-invoice-dollar',
                disabled: false,
              },
              {
                order: 4,
                title: 'PAYOUT_REPORTS',
                name: 'payout-reports',
                url: '/payout-engine/reports',
                activeUrl: '/payout-engine',
                icon: 'fa-chart-line',
                disabled: false,
              },
              {
                order: 5,
                title: 'PAYOUT_PROFILES',
                name: 'payout-profiles',
                url: '/payout-engine/profiles',
                activeUrl: '/payout-engine',
                icon: 'fa-credit-card',
                disabled: false,
              },
              {
                order: 6,
                title: 'PAYOUT_MAPPINGS',
                name: 'payout-mappings',
                url: '/payout-engine/mappings',
                activeUrl: '/payout-engine',
                icon: 'fa-link',
                disabled: false,
                dividerBefore: true,
              },
              // TODO: Gate behind show_payoutengine_provider once flag is configured for launch
              {
                order: 7,
                title: 'PAYMENT_PROVIDERS',
                name: 'payment-providers',
                url: '/payout-engine/providers',
                activeUrl: '/payout-engine',
                icon: 'fa-credit-card',
                disabled: false,
              },
            ],
          });
        }
      }

      if (
        (await this.featureFlagService.isEnabledAsync('show_retail')) &&
        this.canManageRetail()
      ) {
        if (!menus.find((value: any) => value.name === 'retail-management')) {
          menus.push({
            title: 'RETAIL_BILLING',
            name: 'retail-management',
            // Parent opens submenu only (same pattern as payout-engine / dashboard).
            url: '',
            activeUrl: '/retail-management',
            icon: 'fa-shopping-cart',
            icon2: '',
            disabled: false,
            submenus: [
              {
                order: 1,
                title: 'RETAIL_MANAGEMENT',
                name: 'retail-management',
                url: '/retail-management',
                activeUrl: 'retail-management',
                icon: 'fa-box',
                icon2: '',
                disabled: false,
              },
              {
                order: 2,
                title: 'RETAIL_INVENTORY',
                name: 'retail-inventory',
                url: '/retail-inventory',
                activeUrl: 'retail-inventory',
                icon: 'fa-store',
                icon2: '',
                disabled: false,
              },
            ],
          });
        }
      }

      if (await this.featureFlagService.isEnabledAsync('show_corporate')) {
        if (!menus.find((value: any) => value.name === 'contract')) {
          menus.push({
            title: 'CORPORATE',
            name: 'contract',
            url: 'contract',
            activeUrl: 'contract',
            icon: 'fa-file-contract',
            submenus: [
              {
                order: 1,
                title: 'ORGANISATIONS_COMPANIES',
                name: 'company-list',
                url: '/contract/company-list',
                activeUrl: 'company-list',
                showPreview: true,
              },
              {
                order: 2,
                title: 'CONTRACT',
                name: 'contract',
                url: '/contract',
                activeUrl: 'contract',
                showPreview: true,
              },
            ],
          });
        }
      }

      if (await this.featureFlagService.isEnabledAsync('show_audit_trial')) {
        if (!menus.find((value: any) => value.name === 'audit-trail')) {
          menus.push({
            title: 'AUDIT_TRAIL',
            name: 'audit-trail',
            url: '/audit-trail',
            activeUrl: 'audit-trail',
            icon: 'fa-history',
            icon2: '',
            disabled: false,
            submenus: [],
          });
        }
      }

      this.menusSubject.next(menus);
    }
  }

  private canManageRetail(): boolean {
    const claims = this.authService.decodedToken;
    if (!claims) {
      return false;
    }

    if (String(claims.role).toLowerCase() === 'admin') {
      return true;
    }

    return claims.can_manage_retail === true || claims.can_manage_retail === 'true';
  }
}
